import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { editAppeal3 } from "services/prosecutorDecipilineService";
import { Appeal, Complaints } from "types";
import { LoadingIcon } from "utils/constant";
import { APPEAL_STATUS } from "utils/constantValues";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  appealData: Appeal;
  onRefresh: any;
}

export default function AddAppealRegistrationModal({
  isOpen,
  setIsOpen,
  complaintData,
  appealData,
  onRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  // const [success, setSuccess] = useState(false);

  const [validationError, setValidationError] = useState<string | null>(null);

  const [signatureFile, setSignatureFile] = useState<File | null>();
  const [memorendumFile, setMemorendemFile] = useState<File | null>();
  const [formState, setFormState] = useState({
    registration_number: "",
    date: "",
    files: [] as File[],
  });

  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [stampBlob, setStampBlob] = useState<Blob | null>(null);

  useEffect(() => {
    const fetchMemorandumFile = async () => {
      if (appealData?.appeal_documents) {
        const document: any = appealData.appeal_documents.find(
          (doc: any) =>
            doc.document_type === "memorandum_of_disciplinary_appeal",
        );
        if (document?.document_uuid) {
          const documentUrl = `https://discipline-api.oagb.et/api/prosecutor-decipiline/complaint-termination-documents/document/${document.document_uuid}/`;
          try {
            const response = await fetch(documentUrl, {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
            });
            if (!response.ok)
              throw new Error("Failed to fetch memorandum file");
            const blob = await response.blob();
            // Try to infer the file name from the document or fallback
            const fileName =
              document.file_name || "memorandum_of_disciplinary_appeal.pdf";
            const file = new File([blob], fileName, { type: blob.type });
            setMemorendemFile(file);
          } catch (error) {
            console.error("Failed to fetch memorandum file", error);
            setMemorendemFile(null);
          }
        } else {
          setMemorendemFile(null);
        }
      } else {
        setMemorendemFile(null);
      }
    };
    fetchMemorandumFile();
  }, [appealData]);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadStamp = async () => {
      try {
        // Construct the actual API URL – adjust to your real endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL_2}organization/service-stamp/read_stamp/?admin_unit_uuid=${user.admin_unit}&service_name=pdms`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            }, // if needed
          },
        );
        if (!response.ok) throw new Error("Failed to fetch stamp");
        const blob = await response.blob(); // ✅ direct binary, no string corruption
        setStampBlob(blob);
        objectUrl = URL.createObjectURL(blob);
        setStampUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load stamp", error);
      }
    };

    if (isOpen && user) loadStamp();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, user]);

  const signatureFileInputRef = useRef<HTMLInputElement>(null);

  const triggerSignatureFileInput = () => {
    signatureFileInputRef.current?.click();
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!appealData) return;

    // const data = {
    //   // appeal_type: appealData?.appeal_type,
    //   // memorandum_summary: appealData?.memorandum_summary,
    //   // requesting_prosecutor: appealData?.requesting_prosecutor,
    //   registration_number: formState.registration_number,
    //   registration_date: formState.date,
    //   appeal_feedback_status: appealData?.appeal_feedback_status,
    //   appeal_status: APPEAL_STATUS.FORWARDED_TO_REGISTRAR,
    //   discipline_case: appealData?.discipline_case || null,

    //   userid_requesting_prosecutor: appealData?.userid_requesting_prosecutor,
    //   assigned_prosecutor: complaintData?.user_id_accused_prosecutor_name || "",
    //   user_id_assigned_prosecutor:
    //     complaintData?.user_id_accused_prosecutor_name || "",
    //   accused_prosecutor_name: complaintData?.accused_prosecutor_name || "",
    //   user_id_accused_prosecutor_name:
    //     complaintData?.user_id_accused_prosecutor_name || "",
    //   accused_prosecutor_org_id: complaintData?.accused_prosecutor_org_id || "",
    //   accused_prosecutor_admin_unit_id:
    //     complaintData?.accused_prosecutor_admin_unit_id || "",
    //   admin_unit_id: complaintData?.admin_unit_id || "",
    //   org_id: complaintData?.org_id || "",
    // };

    if (!stampBlob) {
      setValidationError("Stamp is required.");
      setLoading(false);
      return;
    }
    if (!signatureFile) {
      setValidationError("Signature file is required.");
      setLoading(false);
      return;
    }
    if (!memorendumFile) {
      setValidationError("Memorandum file is required.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("registration_number", formState.registration_number);
    data.append("date", formState.date);
    data.append("appeal_status", APPEAL_STATUS.FORWARDED_TO_REGISTRAR ?? "");
    data.append("signature1", stampBlob, "stamp.png");
    data.append("signature2", signatureFile);
    data.append("memorandum_files", memorendumFile);

    const appeal_id = appealData?.id || "";
    const isFormData = true;

    setLoading(true);
    // setError(null);
    // setSuccess(false);
    try {
      await editAppeal3(data, appeal_id, isFormData);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      // setSuccess(true);
      setFormState({
        registration_number: "",
        date: "",
        files: [],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
      // setError(
      //   err?.response?.data?.message || err.message || "Something went wrong."
      // );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addAppealRegistrationNo")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        {validationError && (
          <div className="mb-4 text-red-600 font-medium text-sm border border-red-200 bg-red-50 rounded p-2">
            {validationError}
          </div>
        )}
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("date")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="date"
              placeholder={t("date")}
              value={formState.date ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("registrationNo")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="registration_number"
              placeholder={t("registrationNumber")}
              value={formState.registration_number}
              onChange={handleChange}
            />
          </div>

          {/* <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Charge Feedback
            </label>
            <textarea
              required
              rows={7}
              name="feedback"
              value={formState.feedback}
              onChange={handleChange}
              placeholder="Details here..."
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div> */}

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Signature
          </label>
          <div className="file-upload-area" onClick={triggerSignatureFileInput}>
            <input
              type="file"
              ref={signatureFileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSignatureFile(e.target.files[0]);
                }
              }}
              accept=".jpg,.png,.jpeg,.gif"
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">Upload Files</p>
            {signatureFile && (
              <div className="file-list">
                {signatureFile && (
                  <div className="file-item">{signatureFile.name}</div>
                )}
              </div>
            )}
          </div>
        </section>

        {stampUrl && (
          <div className="my-4 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-700 font-semibold">
                Stamp fetched successfully
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("submit")
          )}
        </button>
      </form>
    </SideModal>
  );
}
